import cx from 'clsx';
import React, { useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';

import FacilityTreeBlock from '@components/common/FacilityTreeBlock/FacilityTreeBlock';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { getFacilityStructure } from '@helpers/getFacilityStructure';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IFacilityGroup } from '@model/facilityGroup';

import type { ISaveGroupData } from '@pages/ManageFacility/model/saveFacilityGroup';
import { useCreateEditFacilityGroupMutation } from '@pages/ManageFacility/services/useCreateEditFacilityGroupMutation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import {
  FACILITY_GROUP_FIELDS,
  facilityGroupDefaultValues,
  schemaFacilityGroup,
} from './constants/schemaFacilityGroup';
import style from './style.module.scss';

interface IProps {
  facilityGroup?: IFacilityGroup;
}

const CreateEditFacilityGroupModal: React.FC<IProps> = ({ facilityGroup }) => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();

  const { setModalClose } = useModalActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { mutate: createEditFacilityGroupMutate, isLoading } = useCreateEditFacilityGroupMutation();

  const defaultFormValues = facilityGroupDefaultValues(facilityGroup);

  const facilitiesList = useMemo(() => {
    return getFacilityStructure(assetTreeData?.facilities, 'only-facility');
  }, [assetTreeData]);

  const { control, reset, handleSubmit } = useForm<ISaveGroupData>({
    mode: 'onChange',
    resolver: schemaFacilityGroup(),
    defaultValues: defaultFormValues,
  });

  const {
    fieldState: { error },
  } = useController({
    control,
    name: FACILITY_GROUP_FIELDS.LOCATION_IDS as keyof ISaveGroupData,
  });

  const submit = handleSubmit((data) => {
    createEditFacilityGroupMutate(data, {
      onSuccess: () => {
        const successMessage = !data.id
          ? 'Facility group successfully created.'
          : 'Facility group successfully updated.';
        showToast({ variant: 'success', message: t(successMessage) });
        handleClose();
      },
    });
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const headerTitle = facilityGroup
    ? t('Edit Facility Group') + ` “${facilityGroup.name}”`
    : t('Create Facility Group');

  return (
    <div className={cx(style.facilityGroupModal, { 'body-rtl': rtl })}>
      <ModalHeader title={headerTitle} variant="withoutSeparator" />
      <div className={style.modalInfoLine}>
        {t(
          'Please enter a clear name for this group. Facilities can be assigned to one or more groups to simplify analysis and reporting',
        )}
      </div>
      <div className={style.modalBody}>
        <Input name={FACILITY_GROUP_FIELDS.NAME} control={control} label={t('Group Name')} required />
        <div className={style.facilitiesBlock}>
          <label className={style.facilitiesBlockLabel}>{t('Facility')}</label>
          <FacilityTreeBlock
            control={control}
            locationsFormName={FACILITY_GROUP_FIELDS.LOCATION_IDS}
            facilityStructureData={facilitiesList || []}
            variant="onlyFacilities"
            showSearch={true}
            showSelectAll={true}
          />
          <div className={style.helperText}>{error && t(error?.message)}</div>
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" disabled={isLoading} text={t('Cancel')} onClick={handleClose} />
        <Button
          type="primary"
          disabled={isLoading}
          text={t(facilityGroup?.id ? 'Save' : 'Create')}
          onClick={submit}
        />
      </div>
    </div>
  );
};

export default CreateEditFacilityGroupModal;
